import React, {useCallback, useEffect, useRef, useState} from "react";
import {Alert, Button, Card, Col, Form, Input, Popconfirm, Radio, Result, Row, Select, Spin, Table, Tag, message} from "antd";
import {IntakeService} from "../../../../../shared/api/intake/intake.service";
import {useCanEdit} from "../../../sectionAccess";
import "./IntakeSection.scss";

// «Новое обращение»: the office managers enter a request from Циан, Авито, a landing or a call, and the deal is made
// at once in the right Bitrix funnel (backend service/intake.service.js). While the phone is typed, Bitrix is searched
// for it: an open deal of the same funnel gets the request as a comment instead of a second deal.

const errorOf = (error) => error?.response?.data?.errors || {}
const digits = (value) => String(value || '').replace(/\D/g, '')
const phoneReady = (value) => digits(value).length >= 10
const emailReady = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
const momentOf = (at) => at ? new Date(at).toLocaleString('ru-RU', {timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'}).replace(',', '') : ''
const dayOf = (at) => at ? new Date(at).toLocaleDateString('ru-RU', {timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric'}) : ''

const RESULT_COLORS = {created: 'green', appended: 'blue', failed: 'red'}

// what Bitrix has with the phone: deals (the open ones of this funnel first), open leads
const Matches = ({check, kind}) => {
  if (!check.key) return <p className='intake-muted'>Введите телефон — форма проверит, есть ли он уже в Битриксе.</p>
  if (check.loading) return <div className='intake-muted'><Spin size='small'/> Ищу номер в Битриксе…</div>
  if (check.error) return <Alert type='warning' showIcon message={check.error}/>
  const data = check.data
  if (!data) return null
  if (!data.deals.length && !data.leads.length && !data.contacts.length) {
    return <Alert type='success' showIcon message='Номер новый: в Битриксе его нет'/>
  }
  const deals = [...data.deals].sort((a, b) => Number(b.open && b.sameFunnel) - Number(a.open && a.sameFunnel))
  return (
    <div className='intake-matches'>
      {data.contacts.length > 0 && (
        <p className='intake-muted'>Контакт с этим номером уже есть — второй не создастся.</p>
      )}
      {deals.map((d) => (
        <div key={d.id} className={`intake-deal${d.open && d.sameFunnel ? ' intake-deal--repeat' : ''}`}>
          <div className='intake-deal__head'>
            <a href={d.url} target='_blank' rel='noopener noreferrer'>Сделка №{d.id}</a>
            <Tag color={d.open ? 'green' : 'default'}>{d.open ? 'открыта' : 'закрыта'}</Tag>
            {d.sameFunnel && <Tag color='orange'>эта воронка</Tag>}
          </div>
          <div>{d.funnelName} · {d.stageName}</div>
          <div className='intake-muted'>{d.ownerName || 'без ответственного'} · создана {dayOf(d.createdAt)}</div>
          {d.title && <div className='intake-muted intake-deal__title'>{d.title}</div>}
        </div>
      ))}
      {data.leads.map((l) => (
        <div key={`lead-${l.id}`} className='intake-deal'>
          <div className='intake-deal__head'>
            <a href={l.url} target='_blank' rel='noopener noreferrer'>Лид №{l.id}</a>
            <Tag>{l.statusName}</Tag>
          </div>
          <div className='intake-muted'>{l.ownerName || 'без ответственного'} · создан {dayOf(l.createdAt)}</div>
        </div>
      ))}
      {kind && !kind.blocksOnOpen && data.repeats.length > 0 && (
        <p className='intake-muted'>У агента обычно несколько клиентов: новая сделка создастся, если не выбрать «Добавить в сделку».</p>
      )}
    </div>
  )
}

const Journal = ({journal, onPage}) => (
  <Table
    size='small'
    rowKey='id'
    loading={journal.loading}
    dataSource={journal.items}
    pagination={{current: journal.page, pageSize: 50, total: journal.total, onChange: onPage, showSizeChanger: false, hideOnSinglePage: true}}
    scroll={{x: 900}}
    columns={[
      {title: 'Когда', dataIndex: 'createdAt', width: 120, render: momentOf},
      {title: 'Внесла', dataIndex: 'staff', width: 150},
      {title: 'Кто обращается', dataIndex: 'kind', width: 160},
      {title: 'Откуда', dataIndex: 'source', ellipsis: true},
      {title: 'Клиент', key: 'client', width: 220, render: (_, r) => [r.name, r.phone || r.email].filter(Boolean).join(', ')},
      {title: 'Итог', key: 'result', width: 230, render: (_, r) => (
        <span>
          <Tag color={RESULT_COLORS[r.result]}>{r.resultName}</Tag>
          {r.url && <a href={r.url} target='_blank' rel='noopener noreferrer'>№{r.dealId}</a>}
        </span>
      )},
    ]}
  />
)

export const IntakeSection = () => {
  const canEdit = useCanEdit()
  const [form] = Form.useForm()
  const [options, setOptions] = useState(null)
  const [optionsError, setOptionsError] = useState(null)
  const [check, setCheck] = useState({key: '', loading: false, data: null, error: null})
  const [flat, setFlat] = useState({id: null, loading: false, data: null})
  const [appendTo, setAppendTo] = useState(null)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(null)
  const [showPhone2, setShowPhone2] = useState(false)
  const [journal, setJournal] = useState({items: [], total: 0, page: 1, loading: false})
  const checkTimer = useRef(null)
  const checkSeq = useRef(0)

  const kindKey = Form.useWatch('kind', form)
  const sourceKey = Form.useWatch('source', form)
  const kind = options && options.kinds.find((k) => k.key === kindKey)
  const source = options && options.sources.find((s) => s.key === sourceKey)
  const repeats = (check.data && !check.loading && check.data.deals.filter((d) => d.open && d.sameFunnel)) || []
  const blocks = Boolean(kind && kind.blocksOnOpen && repeats.length)

  const loadJournal = useCallback(async (page = 1) => {
    setJournal((j) => ({...j, loading: true, page}))
    try {
      const {data} = await IntakeService.fetchJournal({page})
      setJournal({items: data.items, total: data.total, page, loading: false})
    } catch (error) {
      setJournal((j) => ({...j, loading: false}))
    }
  }, [])

  useEffect(() => {
    IntakeService.fetchOptions()
      .then(({data}) => setOptions(data))
      .catch((error) => setOptionsError(errorOf(error).message || 'Не удалось загрузить форму. Обновите страницу'))
    loadJournal(1)
  }, [loadJournal])

  // Bitrix is searched a moment after the phone, the second phone, the e-mail or «кто обращается» change
  const runCheck = useCallback(() => {
    const v = form.getFieldsValue(['phone', 'phone2', 'email', 'kind'])
    const params = {
      phone: phoneReady(v.phone) ? v.phone : undefined,
      phone2: phoneReady(v.phone2) ? v.phone2 : undefined,
      email: emailReady(v.email) ? v.email.trim() : undefined,
      kind: v.kind,
    }
    const key = [params.phone, params.phone2, params.email].filter(Boolean).map((x) => digits(x) || x).join('|')
    if (!key) {
      setCheck({key: '', loading: false, data: null, error: null})
      setAppendTo(null)
      return
    }
    const fullKey = `${key}|${params.kind || ''}`
    const seq = ++checkSeq.current
    setCheck({key: fullKey, loading: true, data: null, error: null})
    IntakeService.check(params)
      .then(({data}) => {
        if (seq !== checkSeq.current) return
        setCheck({key: fullKey, loading: false, data, error: null})
        setAppendTo(data.repeats && data.repeats.length ? data.repeats[0] : null)
      })
      .catch((error) => {
        if (seq !== checkSeq.current) return
        setCheck({key: fullKey, loading: false, data: null, error: errorOf(error).message || 'Не удалось проверить номер в Битриксе'})
      })
  }, [form])

  const scheduleCheck = () => {
    clearTimeout(checkTimer.current)
    checkTimer.current = setTimeout(runCheck, 600)
  }
  useEffect(() => () => clearTimeout(checkTimer.current), [])

  const onValuesChange = (changed) => {
    if (['phone', 'phone2', 'email', 'kind'].some((key) => key in changed)) scheduleCheck()
  }

  const lookFlat = async (value) => {
    const id = digits(value)
    form.setFields([{name: 'apartmentId', errors: []}])
    if (!id) {
      setFlat({id: null, loading: false, data: null})
      return
    }
    if (flat.id === id && flat.data) return
    setFlat({id, loading: true, data: null})
    try {
      const {data} = await IntakeService.fetchFlat(id)
      setFlat({id, loading: false, data})
    } catch (error) {
      setFlat({id, loading: false, data: null})
      form.setFields([{name: 'apartmentId', errors: ['Квартиры с таким ID нет в базе']}])
    }
  }

  const reset = () => {
    form.resetFields()
    setDone(null)
    setFlat({id: null, loading: false, data: null})
    setCheck({key: '', loading: false, data: null, error: null})
    setAppendTo(null)
    setShowPhone2(false)
  }

  // the field that stops saving: brought to the middle of the screen and focused, with what is missing in a message
  const showField = (name, text) => {
    form.scrollToField(name, {behavior: 'smooth', block: 'center'})
    const instance = form.getFieldInstance(name)
    if (instance && typeof instance.focus === 'function') {
      setTimeout(() => {
        try {
          instance.focus({preventScroll: true})
        } catch (err) {
          // a control that cannot take focus: the scroll is enough
        }
      }, 400)
    }
    if (text) message.warning(text)
  }

  // mode: 'create' | 'append' | 'force'
  const submit = async (mode) => {
    let v
    try {
      v = await form.validateFields()
    } catch (e) {
      const first = e && e.errorFields && e.errorFields[0]
      if (first) showField(first.name, first.errors[0])
      return
    }
    setSaving(true)
    try {
      const body = {
        kind: v.kind,
        source: v.source,
        sourceText: v.sourceText,
        phone: v.phone,
        phone2: showPhone2 ? v.phone2 : undefined,
        email: v.email,
        name: v.name,
        agency: v.agency,
        buyerName: v.buyerName,
        temperature: v.temperature,
        apartmentId: digits(v.apartmentId) || undefined,
        complexId: flat.data ? undefined : v.complexId,
        comment: v.comment,
        ...(mode === 'append' ? {appendTo} : {}),
        ...(mode === 'force' ? {force: true} : {}),
      }
      const {data} = await IntakeService.create(body)
      setDone(data)
      loadJournal(1)
    } catch (error) {
      const e = errorOf(error)
      if (e.code === 'REPEAT' && e.matches) {
        setCheck((c) => ({...c, loading: false, data: e.matches, error: null}))
        setAppendTo(e.matches.repeats[0] || null)
        message.warning('С этим номером уже есть открытая сделка в этой воронке — выберите, что сделать')
      } else if (e.field && ['kind', 'source', 'sourceText', 'phone', 'phone2', 'email', 'temperature', 'apartmentId', 'complexId'].includes(e.field)) {
        form.setFields([{name: e.field, errors: [e.message]}])
        showField(e.field, e.message)
      } else {
        message.error(e.message || 'Не удалось сохранить. Попробуйте ещё раз')
      }
    }
    setSaving(false)
  }

  if (optionsError) return <Alert type='error' showIcon message={optionsError}/>
  if (!options) return <Spin/>

  if (done) {
    return (
      <div className='intake'>
        <Result
          status='success'
          title={done.result === 'appended' ? `Добавлено в сделку №${done.dealId}` : `Сделка №${done.dealId} создана`}
          subTitle={done.result === 'appended'
            ? `Обращение записано комментарием в открытую сделку, воронка «${done.funnelName}».`
            : `Воронка «${done.funnelName}». ${done.contactCreated ? 'Контакт создан новый.' : 'Контакт уже был в Битриксе — взят он же.'}`}
          extra={[
            <Button key='open' href={done.url} target='_blank' rel='noopener noreferrer'>Открыть в Битриксе</Button>,
            <Button key='next' type='primary' onClick={reset}>Новое обращение</Button>,
          ]}
        />
        <h3 className='intake-journal-title'>Внесённые обращения</h3>
        <Journal journal={journal} onPage={loadJournal}/>
      </div>
    )
  }

  const complexOptions = options.complexes.map((c) => ({value: c.id, label: c.name}))
  const landing = source && source.needs === 'complex'

  return (
    <div className='intake'>
      <Row gutter={24}>
        <Col xs={24} lg={14}>
          <Form form={form} layout='vertical' onValuesChange={onValuesChange} requiredMark={false}>
            <Form.Item name='kind' label='Кто обращается' rules={[{required: true, message: 'Выберите, кто обращается'}]}
              extra={kind ? `Сделка попадёт в воронку «${kind.funnelName}»` : null}>
              <Radio.Group optionType='button' buttonStyle='solid' className='intake-kinds'
                options={options.kinds.map((k) => ({value: k.key, label: k.label}))}/>
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name='phone' label='Телефон' dependencies={['email']}
                  rules={[{validator: (_, value) => {
                    if (value && !phoneReady(value)) return Promise.reject(new Error('Нужно 10 цифр после +7'))
                    if (!value && !String(form.getFieldValue('email') || '').trim()) return Promise.reject(new Error('Нужен телефон или e-mail'))
                    return Promise.resolve()
                  }}]}
                  extra={!showPhone2 && <Button type='link' size='small' className='intake-link' onClick={() => setShowPhone2(true)}>+ ещё телефон</Button>}>
                  <Input placeholder='+7 921 123-45-67' inputMode='tel' allowClear autoFocus/>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='email' label='E-mail'
                  rules={[{type: 'email', message: 'Проверьте e-mail'}]}
                  extra='Если клиент писал без телефона'>
                  <Input allowClear/>
                </Form.Item>
              </Col>
            </Row>
            {showPhone2 && (
              <Form.Item name='phone2' label='Ещё телефон' extra='Например, настоящий номер, если на Авито был подменный'
                rules={[{validator: (_, value) => (
                  !value || phoneReady(value) ? Promise.resolve() : Promise.reject(new Error('Нужно 10 цифр после +7'))
                )}]}>
                <Input placeholder='+7 921 123-45-67' inputMode='tel' allowClear/>
              </Form.Item>
            )}

            <Form.Item name='name' label={kind && kind.isAgent ? 'Имя агента' : 'Имя'}>
              <Input maxLength={120} allowClear/>
            </Form.Item>
            {kind && kind.isAgent && (
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name='agency' label='Агентство'>
                    <Input maxLength={200} allowClear/>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name='buyerName' label={kind.key === 'agentSeller' ? 'ФИО продавца' : 'ФИО покупателя'}>
                    <Input maxLength={200} allowClear/>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name='source' label='Откуда' rules={[{required: true, message: 'Выберите, откуда обращение'}]}>
                  <Select placeholder='Циан, Авито, лендинг…' options={options.sources.map((s) => ({value: s.key, label: s.label}))}/>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                {source && source.needs === 'text' ? (
                  <Form.Item name='sourceText' label='Откуда именно' rules={[{required: true, whitespace: true, message: 'Напишите, откуда обращение'}]}>
                    <Input maxLength={200}/>
                  </Form.Item>
                ) : (
                  <Form.Item name='sourceText' label='Подробнее об источнике' extra='Необязательно: «Авито текст от 03.11», «Jivo-чат»'>
                    <Input maxLength={200} allowClear/>
                  </Form.Item>
                )}
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={10}>
                <Form.Item name='apartmentId' label='Квартира, ID' extra={flat.loading ? 'Ищу квартиру…' : null}>
                  <Input placeholder='461985' inputMode='numeric' allowClear
                    onBlur={(e) => lookFlat(e.target.value)}
                    onPressEnter={(e) => { e.preventDefault(); lookFlat(e.target.value) }}
                    onChange={(e) => !e.target.value && lookFlat('')}/>
                </Form.Item>
              </Col>
              <Col xs={24} md={14}>
                {flat.data ? (
                  <Form.Item label='Квартира'>
                    <div className='intake-flat'>
                      <a href={flat.data.url} target='_blank' rel='noopener noreferrer'>{flat.data.line}</a>
                      {flat.data.status && <Tag>{flat.data.status}</Tag>}
                    </div>
                  </Form.Item>
                ) : (
                  <Form.Item name='complexId' label={landing ? 'ЖК лендинга' : 'Или ЖК'}
                    rules={landing ? [{required: true, message: 'Выберите ЖК лендинга'}] : []}>
                    <Select showSearch allowClear optionFilterProp='label' placeholder='Название ЖК' options={complexOptions}/>
                  </Form.Item>
                )}
              </Col>
            </Row>

            <Form.Item name='temperature' label='Клиент'
              rules={kindKey === 'buyer' ? [{required: true, message: 'Отметьте: горячий или тёплый'}] : []}>
              <Radio.Group optionType='button' options={options.temperatures.map((t) => ({value: t.key, label: t.label}))}/>
            </Form.Item>

            <Form.Item name='comment' label='Комментарий'>
              <Input.TextArea maxLength={3000} autoSize={{minRows: 3, maxRows: 10}} placeholder='Что хочет клиент, бюджет, когда удобно звонить'/>
            </Form.Item>

            {canEdit && (
              <div className='intake-actions'>
                {blocks ? (
                  <>
                    <Button type='primary' loading={saving} disabled={!appendTo || check.loading} onClick={() => submit('append')}>
                      Добавить в сделку №{appendTo}
                    </Button>
                    <Popconfirm title='Создать вторую сделку с этим номером в этой же воронке?' okText='Создать' cancelText='Нет'
                      onConfirm={() => submit('force')}>
                      <Button loading={saving} disabled={check.loading}>Всё равно создать новую</Button>
                    </Popconfirm>
                  </>
                ) : (
                  <>
                    <Button type='primary' loading={saving} disabled={check.loading} onClick={() => submit('create')}>Создать сделку</Button>
                    {repeats.length > 0 && appendTo && (
                      <Button loading={saving} disabled={check.loading} onClick={() => submit('append')}>Добавить в сделку №{appendTo}</Button>
                    )}
                  </>
                )}
              </div>
            )}
          </Form>
        </Col>

        <Col xs={24} lg={10}>
          <Card size='small' title='В Битриксе с этим номером' className='intake-card'>
            <Matches check={check} kind={kind}/>
            {repeats.length > 1 && (
              <div className='intake-choose'>
                <p>Открытых сделок в этой воронке несколько — в какую добавить:</p>
                <Radio.Group value={appendTo} onChange={(e) => setAppendTo(e.target.value)}>
                  {repeats.map((d) => (
                    <Radio key={d.id} value={d.id}>№{d.id} · {d.stageName} · {d.ownerName || 'без ответственного'}</Radio>
                  ))}
                </Radio.Group>
              </div>
            )}
            {blocks && (
              <Alert type='warning' showIcon className='intake-alert'
                message={`В воронке «${kind.funnelName}» уже есть открытая сделка с этим номером`}
                description='Обращение лучше добавить в неё комментарием — ответственный увидит его в сделке.'/>
            )}
          </Card>
        </Col>
      </Row>

      <h3 className='intake-journal-title'>Внесённые обращения</h3>
      <Journal journal={journal} onPage={loadJournal}/>
    </div>
  )
}
